import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Layers,
    RefreshCw,
    ArrowLeft,
    AlertCircle,
    TreePine,
    ChevronRight,
    IndianRupee,
    PieChart,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import KVSLoader from '../../../components/loaders/KVSLoader';
import ExpenseActivityTree from '../components/ExpenseActivityTree';
import { useGetExpenseActivityTreeQuery } from '../services/api';
import { YearDropdown, PhaseDropdown } from '../components/filters';
import type { ExpenseActivityTreeNode } from '../types';

import img1 from '../../../assets/pmshribanner.webp';

const ExpenseActivityTreeScreen: React.FC = () => {
    const navigate = useNavigate();
    const [selectedNode, setSelectedNode] = useState<ExpenseActivityTreeNode | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
    
    // Use RTK Query hook to fetch expense activity tree data
    const { data: response, isLoading, error, refetch } = useGetExpenseActivityTreeQuery({
        ...(selectedYear !== null && { year: selectedYear }),
        ...(selectedPhase !== null && { phase: selectedPhase }),
    });
    const treeData = response?.data?.tree || [];
    // const totalReleasedAmount = response?.data?.totalReleasedAmount || 0;

    // Carousel images
    const carouselImages = [img1];

    // Handle node click
    const handleNodeClick = useCallback((node: ExpenseActivityTreeNode) => {
        setSelectedNode(node);
    }, []);

    // Handle navigation
    // Note: For now keeping same navigation logic as activity tree, 
    // but this might need to change if expense analytics have different routes
    const handleNavigate = useCallback(
        (path: string, node: ExpenseActivityTreeNode) => {
            // Navigate based on node type
            switch (node.type) {
                case 'head':
                    // Navigate to head analytics
                    navigate(`/pm-shri/analytics/heads/H${node.id}`);
                    break;
                case 'subHead':
                    // Extract parent head from path
                    const pathParts = path.split('/').filter(Boolean);
                    if (pathParts.length >= 1) {
                        navigate(`/pm-shri/analytics/heads/H${pathParts[0]}/subheads/${node.id}`);
                    }
                    break;
                case 'activity':
                    // Navigate to activity analytics
                    const activityPathParts = path.split('/').filter(Boolean);
                    if (activityPathParts.length >= 2) {
                        navigate(
                            `/pm-shri/analytics/heads/H${activityPathParts[0]}/subheads/${activityPathParts[1]}/activities/${node.id}`
                        );
                    }
                    break;
                case 'subActivity':
                    // Show details
                    setSelectedNode(node);
                    break;
                default:
                    break;
            }
        },
        [navigate]
    );

    // Parse meta for display
    const parseMetaInfo = (meta: string | null): Record<string, number[]> => {
        if (!meta) return {};
        try {
            return JSON.parse(meta);
        } catch {
            return {};
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 5,
        }).format(amount);
    };

    // Calculate totals
    const totalSanctioned = treeData.reduce((acc, node) => acc + node.totalSanctioned, 0);
    const totalActiveSanctioned = treeData.reduce((acc, node) => acc + node.activeSanctionedAmount, 0);

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-blue-50">
            <Header />
            <HeroCarousel images={carouselImages} />
            <Breadcrumb />

            <main className="flex-grow container mx-auto px-4 py-6">
                {/* Page Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/pm-shri')}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="h-5 w-5 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-2">
                                    <TreePine className="h-7 w-7 text-green-600" />
                                    Expense Activity Tree {selectedYear ? `(${selectedYear}-${(selectedYear + 1).toString().slice(-2)})` : '(All Years)'}
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    View sanctioned and released amounts across the activity hierarchy
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Year Dropdown */}
                            <YearDropdown
                                value={selectedYear}
                                onChange={(val) => setSelectedYear(val)}
                            />
                            {/* Phase Dropdown */}
                            <PhaseDropdown
                                value={selectedPhase}
                                onChange={setSelectedPhase}
                            />
                            <button
                                onClick={() => refetch()}
                                disabled={isLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Financial Summary Cards */}
                {!isLoading && !error && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Sanctioned</p>
                                <p className="text-2xl font-bold text-orange-600">{formatCurrency(totalSanctioned)}</p>
                            </div>
                            <div className="p-3 bg-orange-100 text-orange-600 rounded-full">
                                <IndianRupee className="h-6 w-6" />
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Sanctioned</p>
                                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalActiveSanctioned)}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">Heads marked Continue</p>
                            </div>
                            <div className="p-3 bg-green-100 text-green-600 rounded-full">
                                <IndianRupee className="h-6 w-6" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="font-medium text-red-800">Error Loading Data</p>
                            <p className="text-sm text-red-600">
                                {typeof error === 'object' && 'message' in error 
                                    ? (error as any).message 
                                    : 'Failed to fetch expense activity tree. Please try again later.'}
                            </p>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Loading State */}
                {isLoading && (
                    <div className="flex items-center justify-center h-96">
                        <KVSLoader />
                    </div>
                )}

                {/* Main Content */}
                {!isLoading && !error && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Activity Tree - 2 columns */}
                        <div className="lg:col-span-2">
                            <ExpenseActivityTree
                                data={treeData}
                                isLoading={isLoading}
                                onNodeClick={handleNodeClick}
                                onNavigate={handleNavigate}
                            />
                        </div>

                        {/* Details Panel - 1 column */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 sticky top-4">
                                <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4 rounded-t-xl">
                                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                        <Layers className="h-5 w-5" />
                                        Activity Details
                                    </h3>
                                </div>

                                <div className="p-6">
                                    {selectedNode ? (
                                        <div className="space-y-4">
                                            {/* Title */}
                                            <div>
                                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                    Title
                                                </label>
                                                <p className="text-lg font-semibold text-gray-800 mt-1">
                                                    {selectedNode.title}
                                                </p>
                                            </div>

                                            {/* Stats */}
                                            <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded-lg">
                                                <div>
                                                    <p className="text-xs text-gray-500">Sanctioned</p>
                                                    <p className="font-semibold text-orange-600">{formatCurrency(selectedNode.totalSanctioned)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Released</p>
                                                    <p className="font-semibold text-green-600">{formatCurrency(selectedNode.activeSanctionedAmount)}</p>
                                                </div>
                                            </div>

                                            {/* Type Badge */}
                                            <div>
                                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                    Type
                                                </label>
                                                <div className="mt-1">
                                                    <span
                                                        className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${selectedNode.type === 'head'
                                                            ? 'bg-red-100 text-red-800'
                                                            : selectedNode.type === 'subHead'
                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                : selectedNode.type === 'activity'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-purple-100 text-purple-800'
                                                            }`}
                                                    >
                                                        {selectedNode.type === 'head'
                                                            ? 'HEAD'
                                                            : selectedNode.type === 'subHead'
                                                                ? 'SUBHEAD'
                                                                : selectedNode.type === 'activity'
                                                                    ? 'ACTIVITY'
                                                                    : 'SUBACTIVITY'}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Path */}
                                            {selectedNode.path && (
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                        Path
                                                    </label>
                                                    <p className="text-xs font-mono text-gray-700 mt-1 bg-gray-100 px-3 py-2 rounded-lg break-all">
                                                        {selectedNode.path}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Recurring Type */}
                                            {selectedNode.recurringType !== null && (
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                        Recurring Type
                                                    </label>
                                                    <div className="mt-1">
                                                        <span
                                                            className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${selectedNode.recurringType === 1
                                                                ? 'bg-cyan-100 text-cyan-800'
                                                                : 'bg-rose-100 text-rose-800'
                                                                }`}
                                                        >
                                                            {selectedNode.recurringType === 1
                                                                ? 'Recurring'
                                                                : 'Non-Recurring'}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Available Years */}
                                            {selectedNode.meta && (
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                        Available Years & Phases
                                                    </label>
                                                    <div className="mt-2 space-y-2">
                                                        {Object.entries(parseMetaInfo(selectedNode.meta)).map(
                                                            ([year, phases]) => (
                                                                <div
                                                                    key={year}
                                                                    className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg"
                                                                >
                                                                    <span className="font-medium text-gray-700">{year}</span>
                                                                    <div className="flex gap-1">
                                                                        {(phases as number[]).map((phase) => (
                                                                            <span
                                                                                key={phase}
                                                                                className="px-2 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded"
                                                                            >
                                                                                P{phase}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Children Count */}
                                            {selectedNode.children && selectedNode.children.length > 0 && (
                                                <div>
                                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                        Children
                                                    </label>
                                                    <p className="text-lg font-semibold text-gray-800 mt-1">
                                                        {selectedNode.children.length} items
                                                    </p>
                                                </div>
                                            )}

                                            {/* Navigate Button */}
                                            {selectedNode.path && (
                                                <button
                                                    onClick={() => handleNavigate(selectedNode.path!, selectedNode)}
                                                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-md hover:shadow-lg"
                                                >
                                                    Navigate to Module
                                                    <ChevronRight className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-gray-500">
                                            <PieChart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                                            <p className="font-medium text-gray-600">No Expense Selected</p>
                                            <p className="text-sm mt-1">
                                                Click on any item in the tree to view expense details
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default ExpenseActivityTreeScreen;
